import React, { useState, useEffect } from 'react';
import {
    Box,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Modal,
    Paper,
    Grid2,
    Button,
    CircularProgress,
    Alert,
    Link
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Delete as DeleteIcon, InsertDriveFile } from '@mui/icons-material';
import { useDispatch, useSelector } from "react-redux";
import { fetchOtherTopics, createOtherTopic, deleteOtherTopic } from "../../../../store/startup/aboutProduct/OtherTopicsSlice";
import { useParams } from "react-router-dom";
import { Add } from '@mui/icons-material';
import SingleFileUpload from '../../../fileUpload/FileUpload';
import { toast } from "react-toastify";
import { getFileLink } from '../../../../utils/getFileLink';

const OtherTopics = () => {
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { topics, loading, error } = useSelector(state => state.otherTopics);
    const [open, setOpen] = useState(false);

    const [topic, setTopic] = useState("");
    const [description, setDescription] = useState("");
    const [documentPath, setDocumentPath] = useState("");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (startupId) {
            dispatch(fetchOtherTopics(startupId));
        }
    }, [dispatch, startupId]);

    const validateForm = () => {
        const newErrors = {};
        if (!topic.trim()) newErrors.topic = "Belge No boş bırakılamaz";
        if (!description.trim()) newErrors.description = "Açıklama boş bırakılamaz";
        if (!documentPath) newErrors.documentPath = "Lütfen bir dosya yükleyin";
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            await dispatch(createOtherTopic({
                startupId,
                topic,
                description,
                documentPath
            })).unwrap();
            handleClose();
            toast.success('Konu başarıyla eklendi');
        } catch (error) {
            console.error('Failed to save topic:', error);
            toast.error('Konu eklenirken bir hata oluştu');
        }
    };

    const handleDelete = async (additionalTopicId) => {
        try {
            await dispatch(deleteOtherTopic(additionalTopicId)).unwrap();
            toast.success('Konu başarıyla silindi');
        } catch (error) {
            console.error('Failed to delete topic:', error);
            toast.error('Konu silinirken bir hata oluştu');
        }
    };

    const handleDocumentUpload = (filePath) => {
        setDocumentPath(filePath);
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setTopic("");
        setDescription("");
        setDocumentPath("");
        setErrors({});
    };

    return (
        <div>
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ flexGrow: 1, width: '80%' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Ürün/Proje ile İlgili Belirtmek İstediğiniz Diğer Konular
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right', width: '20%', height: '1%' }}>
                    <Button variant="outlined" size="small" color="primary" startIcon={<Add />} onClick={handleOpen}>
                        Yeni Ekle
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ marginBottom: 3 }} />
            
            <Box>
                <TableContainer component={Paper} sx={{ marginBottom: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Belge No</TableCell>
                                <TableCell>Açıklama</TableCell>
                                <TableCell>Dosya</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {topics?.map((item) => (
                                <TableRow key={item.additionalTopicId}>
                                    <TableCell>{item.topic}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>
                                        {item.documentPath && (
                                            <Link 
                                                href={getFileLink(item.documentPath)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                <InsertDriveFile sx={{ mr: 1 }} />
                                                Dosyayı Görüntüle
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button 
                                            size="small" 
                                            color="error" 
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleDelete(item.additionalTopicId)}
                                        >
                                            Sil
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
                sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 800,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                }}>
                    <Typography id="modal-title" variant="h6" sx={{ mb: 2 }}>
                        Yeni Belge Ekle
                    </Typography>

                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 8 }}>
                            <TextField
                                label="Belge No"
                                fullWidth
                                sx={{ marginBottom: 2 }}
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                error={!!errors.topic}
                                helperText={errors.topic}
                            />
                            <TextField
                                label="Açıklama"
                                fullWidth
                                multiline
                                rows={4}
                                sx={{ marginBottom: 2 }}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={!!errors.description}
                                helperText={errors.description}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 4 }}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
                                <SingleFileUpload 
                                    onUploadComplete={handleDocumentUpload}
                                    existFilePath={documentPath}
                                    fileUploadPath="startup"
                                />
                            </Box>
                            {errors.documentPath && (
                                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                    {errors.documentPath}
                                </Typography>
                            )}
                        </Grid2>
                    </Grid2>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button variant="outlined" color="error" onClick={handleClose}>
                            İptal
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default OtherTopics;
